import React, { useContext, useEffect, useState } from 'react';

// Components
import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartImageUpload from '@component/smartForm/SmartImageUpload';
import SmartContainer from './smartForm/SmartContainer';
import RoleDescriptionList from './RoleDescriptionList';

// Contexts & Hooks
import { AccountContext } from "@context/AccountProvider";
import { AlertContext } from "@context/AlertProvider";
import { useTranslation } from "react-i18next";

// Queries & Mutations
import { useAccountUpdateMutation } from '@mutation/accountMutation';

// Services, Utils & Validators
import { mapRole } from '@service/roleService';

import { removeEmptyValues } from '@utils/generalUtils';
import { useQueryClient } from "@tanstack/react-query";
import { nameValidator } from './smartForm/fieldValidators';

// 👈 Added onCancel prop for better integration with SidePanel
function MyAccountForm() {
    const [formModel, setFormModel] = useState(null);

    const { account } = useContext(AccountContext);
    const { addAlertBanner } = useContext(AlertContext);
    const { t } = useTranslation(["account", "main", "role", "error"]);

    const queryClient = useQueryClient();

    const accountUpdateMutation = useAccountUpdateMutation();

    useEffect(() => {
        setFormModel(account);
    }, [account]);

    const handleSave = (newModel) => {
        const cleaned = removeEmptyValues({
            first_name: newModel.first_name,
            last_name: newModel.last_name,
            file_image_uuid: newModel.file_image_uuid
        });

        accountUpdateMutation.mutate({ data: { ...cleaned } }, {
            onSuccess: () => {
                addAlertBanner(t("main:saveSuccess"), "success", 4000);
                queryClient.invalidateQueries({ queryKey: ["me"] });
            },
            onError: (error) => {
                setFormModel(account);
                if (!error.response) {
                    addAlertBanner(t("error:networkError"), "error", 4000);
                    return;
                }

                const statusCode = error.response.status;

                if (statusCode === 403) {
                addAlertBanner(t("error:noModificationPrivileges"), "error", 5000);
                } else if (statusCode === 404) {
                addAlertBanner(t("error:notFound"), "error", 4000);
                } else if (statusCode === 400) {
                addAlertBanner(t("error:validationError"), "error", 5000);
                } else if (statusCode === 422) {
                addAlertBanner(t("error:validationError"), "error", 5000);
                } else if (statusCode >= 500) {
                addAlertBanner(t("error:serverError"), "error", 4000);
                } else {
                addAlertBanner(t("error:somethingGoesWrong"), "error", 4000);
                }
            }
        });
    };

    const getAccountRoleTranslationKey = () => {
        if (mapRole(account?.role_name)) {
          return mapRole(account?.role_name)['translationKey'];
        }
        return null;
      }

    // 👈 Correctly determine the loading state from all relevant queries/mutations
    const isLoading = accountUpdateMutation.isPending;

    if (!account) return null;

    return (
        <SmartForm
            formModel={formModel}
            setFormModel={setFormModel}
            onSave={handleSave}
            isLoading={isLoading}
            buttonPanelSx={{
                top: "10px",
                right: "10px",
            }}
        >
            <SmartContainer sx={{ flex: 1 }}>
                <SmartImageUpload
                    field="file_image_uuid"
                    imageSx={{
                        width: "auto",
                        minHeight: "310px",
                        objectFit: "contain",
                        backgroundColor:"#fff"
                    }}
                />
            </SmartContainer>

            <SmartContainer 
                sx={{ flex: 1, p: 2 }} 
                contentSx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, 170px)',
                    gap: 1,
                }}
            >
                <SmartTextInput field="first_name" label={t("account:name")} validator={nameValidator}/>
                <SmartTextInput field="last_name" label={t("account:lastName")} validator={nameValidator}/>
                <SmartTextInput field="email" label={t("account:email")} editable={false} />
                <SmartTextInput field="role_name" label={t("account:accountRole")}  value={t(getAccountRoleTranslationKey())} editable={false} />
            </SmartContainer>  

            <SmartContainer sx={{ flex: 1, p: 2  }}>
                <RoleDescriptionList/> 
            </SmartContainer>
        </SmartForm>
    );
}

export default MyAccountForm;